import { generateClient } from 'aws-amplify/api';
import {
  getUserWatchlistQuery,
  getUserWatchedQuery,
  createWatchlistItemMutation,
  createWatchedItemMutation,
  deleteWatchlistItemMutation,
  deleteWatchedItemMutation
} from '../graphql/watchlist';
import axios from 'axios';

// Lazy client initialization to ensure Amplify is configured first
let client = null;
const getClient = () => {
  if (!client) {
    client = generateClient();
  }
  return client;
};
const MONGO_API_BASE = 'https://hype-circle-app-cloud-913961422896.europe-west1.run.app/api';

export const getUserWatchlist = async (userID, limit = 20, nextToken = null) => {
  try {
    const result = await getClient().graphql({
      query: getUserWatchlistQuery,
      variables: { userID, limit, nextToken }
    });
    
    const items = result.data.listWatchlistItems.items;
    const itemsWithContent = await Promise.all(
      items.map(async (item) => {
        try {
          if (item.contentType === 'MOVIE' && item.movieID) {
            const movieResponse = await axios.get(`${MONGO_API_BASE}/movies/${item.movieID}`);
            return { ...item, movie: movieResponse.data };
          } else if (item.contentType === 'SHOW' && item.showID) {
            const showResponse = await axios.get(`${MONGO_API_BASE}/shows/${item.showID}`);
            return { ...item, show: showResponse.data };
          }
          return item;
        } catch (error) {
          console.error(`Error fetching content for item ${item.id}:`, error);
          return item;
        }
      })
    );
    
    return {
      items: itemsWithContent,
      nextToken: result.data.listWatchlistItems.nextToken
    };
  } catch (error) {
    console.error('Error fetching watchlist:', error);
    throw error;
  }
};

export const getUserWatched = async (userID, limit = 20, nextToken = null) => {
  try {
    const result = await getClient().graphql({
      query: getUserWatchedQuery,
      variables: { userID, limit, nextToken }
    });
    
    const items = result.data.listWatchedItems.items;
    console.log(`getUserWatched: Found ${items.length} watched items for user ${userID}`);
    
    const itemsWithContent = await Promise.all(
      items.map(async (item) => {
        try {
          if (item.movieID) {
            const movieResponse = await axios.get(`${MONGO_API_BASE}/movies/${item.movieID}`);
            return { ...item, movie: movieResponse.data };
          }
          return item;
        } catch (error) {
          console.error(`Error fetching movie ${item.movieID}:`, error);
          return item;
        }
      })
    );
    
    return {
      items: itemsWithContent,
      nextToken: result.data.listWatchedItems.nextToken
    };
  } catch (error) {
    console.error('Error fetching watched items:', error);
    console.error('Error details:', error.errors || error.message);
    throw error;
  }
};

export const addToWatchlist = async (userID, movieID, status = 'WANT_TO_WATCH') => {
  try {
    const result = await getClient().graphql({
      query: createWatchlistItemMutation,
      variables: {
        input: { userID, movieID, status }
      }
    });
    return result.data.createWatchlistItem;
  } catch (error) {
    console.error('Error adding to watchlist:', error);
    throw error;
  }
};

export const addToWatched = async (userID, movieID, status = 'WATCHED') => {
  try {
    const result = await getClient().graphql({
      query: createWatchedItemMutation,
      variables: {
        input: { userID, movieID, status }
      }
    });
    return result.data.createWatchedItem;
  } catch (error) {
    console.error('Error adding to watched:', error);
    throw error;
  }
};

export const removeFromWatchlist = async (itemId) => {
  try {
    const result = await getClient().graphql({
      query: deleteWatchlistItemMutation,
      variables: {
        input: { id: itemId }
      }
    });
    return result.data.deleteWatchlistItem;
  } catch (error) {
    console.error('Error removing from watchlist:', error);
    throw error;
  }
};

export const removeFromWatched = async (itemId) => {
  try {
    const result = await getClient().graphql({
      query: deleteWatchedItemMutation,
      variables: {
        input: { id: itemId }
      }
    });
    return result.data.deleteWatchedItem;
  } catch (error) {
    console.error('Error removing from watched:', error);
    throw error;
  }
};

export const getMovieSections = async (groupBy = 'genre', params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      group_by: groupBy,
      per_page: params.per_page || 500,
      ...params
    });
    const response = await axios.get(`${MONGO_API_BASE}/movies/sections?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching movie sections:', error);
    return { groups: [] };
  }
};

export const getGroupMovies = async (groupBy, groupKey, params = {}) => {
  try {
    const queryParams = new URLSearchParams({
      per_page: params.per_page || 1000,
      ...params
    });
    const response = await axios.get(`${MONGO_API_BASE}/movies/group/${groupBy}/${encodeURIComponent(groupKey)}?${queryParams}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching group movies:', error);
    return { movies: [] };
  }
};

export const watchlistApi = {
  getUserWatchlist,
  getUserWatched,
  addToWatchlist,
  addToWatched,
  removeFromWatchlist,
  removeFromWatched,
  getMovieSections,
  getGroupMovies
};